'use client';

import { ArrowDownNarrowWide, ArrowDownUp, ArrowDownWideNarrow, Locate } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { InputSearch } from '@//components/InputSearch';
import FilterSortContainer from '@//containers/FilterSort';
import useDevicesGeoPosition from '@//data/geo/useDevicesGeoPosition';
import type { ElasticSearchResponseDocumentListSortOption } from '@//ElasticSearch';
import { useMarketMetadata } from '@//providers/MarketSettingsProvider';
import styles from '../../RetailersPage.module.scss';

type FormFields = { q: string };

const getSortIcon = (sortValue: string | undefined) => {
  switch (sortValue) {
    case '':
    case 'priceDefaultDesc':
    case 'modelYearDesc':
    case 'mileageDesc':
      return <ArrowDownWideNarrow aria-hidden="true" />;

    case 'priceDefaultAsc':
    case 'modelYearAsc':
    case 'mileageAsc':
      return <ArrowDownNarrowWide aria-hidden="true" />;
    default:
      return <ArrowDownUp aria-hidden="true" />;
  }
};

interface SearchBarProps {
  totalHits?: number;
  sortOptions: ElasticSearchResponseDocumentListSortOption[];
  initialQuery?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({ totalHits, initialQuery = '', sortOptions }) => {
  const params = useSearchParams();
  const router = useRouter();
  const marketMetadata = useMarketMetadata();
  const t = useTranslations('pages.retailers.searchBar');

  const geoQuery = useDevicesGeoPosition();
  const geoPosition = geoQuery.data;
  const [loading, setLoading] = useState(false);

  const { register, getValues } = useForm<FormFields>({
    defaultValues: { q: initialQuery },
  });

  const latLon = useMemo(() => {
    if (geoPosition?.coords?.latitude && geoPosition?.coords?.longitude) {
      return {
        lat: geoPosition.coords.latitude.toFixed(2),
        lon: geoPosition.coords.longitude.toFixed(2),
      };
    }
    return null;
  }, [geoPosition]);

  const hasCorrectGeo = useMemo(() => {
    const latParam = params.get('lat');
    const lonParam = params.get('lon');
    return latLon && latParam === latLon.lat && lonParam === latLon.lon;
  }, [params, latLon]);

  const buildSearchURL = useCallback(
    ({
      q,
      withGeo = false,
      removeOffset = false,
      sort,
    }: {
      q?: string;
      withGeo?: boolean;
      removeOffset?: boolean;
      sort?: string | null;
    }) => {
      const urlSearchParams = new URLSearchParams(params);
      if (q) urlSearchParams.set('q', q);
      else urlSearchParams.delete('q');
      if (withGeo && latLon) {
        urlSearchParams.set('lat', latLon.lat);
        urlSearchParams.set('lon', latLon.lon);
      } else {
        urlSearchParams.delete('lat');
        urlSearchParams.delete('lon');
      }
      if (removeOffset) urlSearchParams.delete('offset');
      if (sort) urlSearchParams.set('sort', sort);
      else urlSearchParams.delete('sort');
      return `${marketMetadata.pageLinks.retailersPage}?${urlSearchParams}`;
    },
    [params, latLon, marketMetadata.pageLinks.retailersPage],
  );

  const hasAutoLocated = useRef(false);

  useEffect(() => {
    if (latLon && !hasAutoLocated.current && !hasCorrectGeo) {
      hasAutoLocated.current = true;
      setLoading(true);
      router.push(buildSearchURL({ q: getValues('q'), withGeo: true }));
    }
  }, [latLon, getValues, router, buildSearchURL, hasCorrectGeo]);

  const handleSubmit = useCallback(
    (q: string) => {
      setLoading(true);
      router.push(buildSearchURL({ q, withGeo: !!latLon, removeOffset: true }));
    },
    [router, buildSearchURL, latLon],
  );

  const handleSortChange = useCallback(
    (sortValue: string) => {
      setLoading(true);
      router.push(
        buildSearchURL({
          q: getValues('q'),
          withGeo: !!latLon,
          removeOffset: true,
          sort: sortValue || null,
        }),
      );
    },
    [router, buildSearchURL, latLon, getValues],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleSubmit(getValues('q'));
      }
    },
    [handleSubmit, getValues],
  );

  const handleUseLocation = useCallback(() => {
    if (latLon && !hasCorrectGeo) {
      setLoading(true);
      router.push(buildSearchURL({ q: getValues('q'), withGeo: true }));
    }
  }, [latLon, hasCorrectGeo, router, buildSearchURL, getValues]);

  useEffect(() => {
    setLoading(false);
  }, [params.toString()]);

  const currentSort = useMemo(() => {
    const selected = sortOptions.find((x) => x.selected);
    return selected ? new URLSearchParams(selected.query).get('sort') || '' : '';
  }, [sortOptions]);

  return (
    <div className={styles.searchBar}>
      <InputSearch
        id="retailers-search"
        label={t('label')}
        placeholder={t('placeholder')}
        register={register('q')}
        onKeyDown={handleKeyDown}
        suggestionExpanded={false}
        loading={loading}
        button={{
          label: (
            <>
              <Locate aria-hidden="true" />
              <span className="sr-only">{t('useMyPosition')}</span>
            </>
          ),
          title: t('useMyPosition'),
          onClick: handleUseLocation,
          disabled: latLon && hasCorrectGeo ? true : undefined,
        }}
        limitWidth
      />
      <div className={styles.searchBarActions}>
        <p className={styles.totalHits}>{t('totalHits', { totalHits: totalHits ?? 0 })}</p>
        <FilterSortContainer
          sortOptions={sortOptions}
          currentSort={currentSort}
          onSortChange={handleSortChange}
          getSortIcon={getSortIcon}
          buttonTitle={t('sortBy')}
          buttonAriaLabel={`${t('sortBy')}: {currentSort}`}
          buttonPrefix={t('sortBy')}
          buttonUnknown={t('sortBy')}
          menuAriaLabel={t('sortBy')}
          menuOptionTitle={'{option}'}
        />
      </div>
    </div>
  );
};

export default SearchBar;
